import {
  createTheme,
  CSSObject,
  Palette,
  PaletteColor,
  PaletteColorOptions,
} from '@mui/material';

export const customBreakpoints = {
  xs: 0,
  sm: 600,
  md: 900,
  lg: 1285,
  xl: 1536,
};

/** Base radius for controls and nested surfaces */
const BORDER_RADIUS = 8;
/** Dialog paper: concentric with ~16px content inset around controls at BORDER_RADIUS */
const DIALOG_RADIUS = 16;
/** Elevated menus / popovers sit closer to controls */
const MENU_RADIUS = 10;

/** better-ui / Emil: ease-out that starts fast for perceived responsiveness */
const EASE_OUT = 'cubic-bezier(0.2, 0, 0, 1)';
const PRESS_SCALE = '0.96';
const PRESS_TRANSITION =
  'transform 150ms cubic-bezier(0.2, 0, 0, 1), opacity 150ms cubic-bezier(0.2, 0, 0, 1)';

const elevationShadow = (mode: 'dark' | 'light') =>
  mode === 'dark'
    ? '0 0 0 1px rgba(255,255,255,0.06), 0 8px 24px rgba(0,0,0,0.45)'
    : '0 0 0 1px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.08)';

const softElevationShadow = (mode: 'dark' | 'light') =>
  mode === 'dark'
    ? '0 0 0 1px rgba(255,255,255,0.05), 0 4px 12px rgba(0,0,0,0.35)'
    : '0 0 0 1px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.06)';

/**
 * Thin, theme-coloured scrollbars for the whole app. Chromium and Safari use
 * ::-webkit-scrollbar (rounded thumb, hover state). The standard properties go
 * only to engines without it (Firefox): in Chromium 121+ setting them would
 * switch the ::-webkit-scrollbar styling off. Only the look changes; overflow
 * modes stay as they are, so there is no layout shift.
 */
export const scrollbarStyles = (palette: Palette): CSSObject => ({
  '@supports not selector(::-webkit-scrollbar)': {
    '*': {
      scrollbarWidth: 'thin',
      scrollbarColor: `${palette.scrollbar.thumb} transparent`,
    },
  },
  '::-webkit-scrollbar': {
    width: 8,
    height: 8,
  },
  '::-webkit-scrollbar-track, ::-webkit-scrollbar-corner': {
    background: 'transparent',
  },
  '::-webkit-scrollbar-thumb': {
    backgroundColor: palette.scrollbar.thumb,
    backgroundClip: 'padding-box',
    border: '2px solid transparent',
    borderRadius: 8,
  },
  '::-webkit-scrollbar-thumb:hover': {
    backgroundColor: palette.scrollbar.thumbHover,
  },
});

/** Text, tint and border of a status chip. */
export type StatusTone = { text: string; bg: string; border: string };

export type TagColorKey =
  | 'per'
  | 'loc'
  | 'org'
  | 'date'
  | 'phone'
  | 'email'
  | 'link';

/**
 * Colours of the review canvas. Everything under `page*`, `redaction`,
 * `highlight`, `hover`, `selected`, `notSure`, `tags` and `popover` is drawn
 * on (or floats over) the PDF page image, which is white in both themes, so
 * those values are the same in light and dark. They reach the editor iframe
 * as CSS variables (src/utils/editPageCssVars.ts).
 */
export type ReviewPalette = {
  /** The PDF page image itself (contrast reference for on-page colours). */
  page: string;
  /** Around the pages in the editor. */
  canvas: string;
  /** Edge of a page and of a thumbnail against the canvas. */
  pageOutline: string;
  /**
   * Box marked for redaction in the editor: a light translucent grey so the
   * reviewer can still read the words under it, and a solid dark edge that
   * carries the "redacted" signal. Editor only: the exported PDF is always
   * solid opaque black (services/generate-output/redactor.py).
   */
  redaction: { editorFill: string; border: string };
  /** Search hits ("highlight"): current hit uses the active pair. */
  highlight: {
    fill: string;
    border: string;
    ring: string;
    activeFill: string;
    activeBorder: string;
    activeRing: string;
  };
  /** Text-selection hover over a box. */
  hover: { fill: string; border: string };
  /** Selected item / text being selected. */
  selected: { fill: string; border: string };
  /** "Not sure" markers: dashed amber outline, tint and "?" badge. */
  notSure: {
    fill: string;
    border: string;
    ring: string;
    badge: string;
    badgeText: string;
    reviewed: string;
  };
  /** Category outlines (suggestions) and their corner dot. */
  tags: Record<TagColorKey, string>;
  tagDotBorder: string;
  /** Amber "check manually" notice and thumbnail badges. */
  notice: { text: string; bg: string; border: string };
  /** Detection cards in the editor list. */
  listItem: {
    redacted: string;
    suggested: string;
    hover: string;
    legalCode: string;
  };
  /** Action strip that opens on a box inside the page. */
  popover: {
    bg: string;
    border: string;
    divider: string;
    text: string;
    buttonBorder: string;
    hover: string;
    shadow: string;
  };
  /** Corner grips of a box. */
  handle: { fill: string; border: string; ring: string };
  /** Page number under a thumbnail. */
  thumbBadge: { bg: string; text: string; border: string };
  /** Veil over the canvas while pages load. */
  veil: string;
  /** Inner glow of the thumbnail-strip divider. */
  dividerGlow: string;
};

/** Colours drawn on the white PDF page: identical in both themes. */
const pageColors = {
  page: '#ffffff',
  // Was 0.3: lighter so the ink under a redaction reads at 12:1 (was 9.5:1).
  redaction: { editorFill: 'rgba(17, 24, 39, 0.2)', border: '#111827' },
  highlight: {
    fill: 'rgba(33, 150, 243, 0.4)',
    border: '#2196f3',
    ring: 'rgba(33, 150, 243, 0.9)',
    activeFill: 'rgba(13, 71, 161, 0.5)',
    activeBorder: '#0d47a1',
    activeRing: 'rgba(13, 71, 161, 0.95)',
  },
  hover: { fill: 'rgba(0, 138, 220, 0.12)', border: 'rgba(0, 138, 220, 0.7)' },
  selected: { fill: 'rgba(0, 128, 0, 0.12)', border: '#008000' },
  notSure: {
    fill: 'rgba(217, 119, 6, 0.1)',
    border: '#b45309',
    ring: 'rgba(180, 83, 9, 0.35)',
    badge: '#92400e',
    badgeText: '#ffffff',
    reviewed: '#6b7280',
  },
  // Darker than the old pastels (1.1-2.0:1 on the page) so every outline
  // reaches 3:1 on white; hues kept, and away from the "not sure" amber.
  tags: {
    per: '#0f766e',
    loc: '#6d28d9',
    org: '#e8590c',
    date: '#8a7300',
    phone: '#15803d',
    email: '#be123c',
    link: '#4d7c0f',
  },
  tagDotBorder: '#333333',
  popover: {
    bg: '#20293f',
    border: 'rgba(255, 255, 255, 0.12)',
    divider: 'rgba(255, 255, 255, 0.1)',
    text: '#ffffff',
    buttonBorder: 'rgba(255, 255, 255, 0.35)',
    hover: 'rgba(255, 255, 255, 0.12)',
    shadow: 'rgba(0, 0, 0, 0.35)',
  },
  handle: {
    fill: '#2f6fed',
    border: '#ffffff',
    ring: 'rgba(47, 111, 237, 0.35)',
  },
  thumbBadge: { bg: '#ffffff', text: '#000000', border: '#808080' },
  notice: { text: '#92400e', bg: '#fef3c7', border: '#92400e' },
  veil: 'rgba(255, 255, 255, 0.55)',
};

const reviewPalette = (mode: 'dark' | 'light'): ReviewPalette => ({
  ...pageColors,
  canvas: mode === 'dark' ? '#0e172d' : '#e4ebf7',
  pageOutline: mode === 'dark' ? 'transparent' : '#6b7686',
  listItem:
    mode === 'dark'
      ? {
          redacted: 'rgba(0, 0, 0, 0.22)',
          suggested: 'rgba(0, 0, 0, 0.4)',
          hover: 'rgba(0, 0, 0, 0.55)',
          legalCode: '#008adc',
        }
      : {
          redacted: 'rgba(15, 23, 42, 0.06)',
          suggested: 'rgba(15, 23, 42, 0.12)',
          hover: 'rgba(15, 23, 42, 0.18)',
          legalCode: '#00548c',
        },
  dividerGlow:
    mode === 'dark' ? 'rgba(255, 255, 255, 0.25)' : 'rgba(15, 23, 42, 0.12)',
});

const statusPalette = (mode: 'dark' | 'light') =>
  mode === 'dark'
    ? {
        primary: {
          text: '#00a0ff',
          bg: 'rgba(0, 138, 220, 0.14)',
          border: 'rgba(0, 138, 220, 0.4)',
        },
        info: {
          text: '#00C2FF',
          bg: 'rgba(0, 194, 255, 0.12)',
          border: 'rgba(0, 194, 255, 0.35)',
        },
        warning: {
          text: '#FFC700',
          bg: 'rgba(255, 199, 0, 0.12)',
          border: 'rgba(255, 199, 0, 0.4)',
        },
        success: {
          text: '#3dd68c',
          bg: 'rgba(61, 214, 140, 0.12)',
          border: 'rgba(61, 214, 140, 0.4)',
        },
        error: {
          text: '#F83232',
          bg: 'rgba(248, 50, 50, 0.12)',
          border: 'rgba(248, 50, 50, 0.35)',
        },
      }
    : {
        primary: {
          text: '#1e42a8',
          bg: 'rgba(41, 87, 203, 0.1)',
          border: 'rgba(30, 66, 168, 0.35)',
        },
        info: {
          text: '#0369a1',
          bg: 'rgba(0, 194, 255, 0.12)',
          border: 'rgba(3, 105, 161, 0.35)',
        },
        warning: {
          text: '#854d0e',
          bg: 'rgba(255, 199, 0, 0.16)',
          border: 'rgba(133, 77, 14, 0.35)',
        },
        success: {
          text: '#166534',
          bg: 'rgba(61, 214, 140, 0.14)',
          border: 'rgba(22, 101, 52, 0.35)',
        },
        error: {
          text: '#b91c1c',
          bg: 'rgba(248, 50, 50, 0.1)',
          border: 'rgba(185, 28, 28, 0.35)',
        },
      };

export type StatusPalette = ReturnType<typeof statusPalette>;

/** Light/dark-specific colours for smaller UI parts. */
const surfacePalette = (mode: 'dark' | 'light') => ({
  /**
   * Page background decoration (anonify.nl raster and top glow). Light only:
   * dark mode keeps its plain background, so these are transparent there.
   */
  backdrop: {
    gridLine: mode === 'dark' ? 'transparent' : 'rgba(41, 87, 203, 0.06)',
    heroGlow: mode === 'dark' ? 'transparent' : 'rgba(41, 87, 203, 0.18)',
    gridSize: '4rem 4rem',
  },
  /** Card and table hairlines. */
  hairline: mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(41, 87, 203, 0.16)',
  scrollbar:
    mode === 'dark'
      ? { thumb: 'rgba(255, 255, 255, 0.3)', thumbHover: 'rgba(255, 255, 255, 0.5)' }
      : { thumb: 'rgba(15, 23, 42, 0.5)', thumbHover: 'rgba(15, 23, 42, 0.65)' },
  /** Sortable table headers (users, companies) and their divider line. */
  tableHeader:
    mode === 'dark'
      ? {
          label: 'rgba(255, 255, 255, 0.5)',
          labelHover: 'rgba(255, 255, 255, 1)',
          divider:
            'linear-gradient(90deg, rgba(53,58,66,0) 2.5%, rgba(255,255,255,1) 50%, rgba(53,58,66,0) 97.5%)',
        }
      : {
          label: 'rgba(0, 0, 0, 0.6)',
          labelHover: 'rgba(0, 0, 0, 0.87)',
          divider:
            'linear-gradient(90deg, rgba(15,23,42,0) 2.5%, rgba(15,23,42,0.35) 50%, rgba(15,23,42,0) 97.5%)',
        },
  /** Editor search bar: scope toggle, restore button, input. */
  searchBar:
    mode === 'dark'
      ? {
          text: '#ffffff',
          border: 'rgba(255, 255, 255, 0.28)',
          selectedBg: 'rgba(255, 255, 255, 0.18)',
          selectedHoverBg: 'rgba(255, 255, 255, 0.24)',
          outlineBorder: 'rgba(255, 255, 255, 0.35)',
          inputBg: 'rgba(255, 255, 255, 0.4)',
          divider: 'rgba(255, 255, 255, 0.08)',
        }
      : {
          text: 'rgba(0, 0, 0, 0.87)',
          border: 'rgba(15, 23, 42, 0.5)',
          selectedBg: 'rgba(15, 23, 42, 0.1)',
          selectedHoverBg: 'rgba(15, 23, 42, 0.14)',
          outlineBorder: 'rgba(15, 23, 42, 0.55)',
          inputBg: 'rgba(255, 255, 255, 0.4)',
          divider: 'rgba(15, 23, 42, 0.12)',
        },
  /** Language code in the top bar. */
  languageLabel: mode === 'dark' ? '#9e9e9e' : 'rgba(0, 0, 0, 0.6)',
  /** "Active" / "Suspended" in the users table. */
  userState:
    mode === 'dark'
      ? { active: '#4caf50', suspended: '#f44336' }
      : { active: '#166534', suspended: '#b91c1c' },
  /** Tinted surfaces that used to be hard-coded per component. */
  tint: {
    unseen: mode === 'dark' ? 'rgba(0, 138, 220, 0.06)' : 'rgba(41, 87, 203, 0.07)',
    createCard: mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'rgba(41, 87, 203, 0.04)',
    closeButton: 'rgba(180, 179, 179, 0.2)',
    location: '#FF84B8',
  },
  shadow: {
    card: mode === 'dark' ? '0 2px 10px rgba(0,0,0,0.3)' : '0 2px 10px rgba(0,0,0,0.06)',
    subItem: '0px 4px 4px rgba(0, 0, 0, 0.08), 0px 4px 8px rgba(50, 50, 71, 0.006)',
    banner: '0 4px 4px 0 rgba(0,0,0,0.25)',
    toggle: '0 3px 6px rgba(0,0,0,0.25), 0 2px 2px rgba(0,0,0,0.22)',
  },
  /** Full-screen loading veil. */
  veil: mode === 'dark' ? 'rgba(14, 23, 45, 0.8)' : 'rgba(255, 255, 255, 0.8)',
  /** Dark "manage members" button and the unused full-screen dialog. */
  strongSurface: '#353A42',
  fullScreenDialog: '#282C31',
  /** Progress bar shimmer. */
  shimmer:
    'linear-gradient(90deg, rgba(255,255,255,0.12) 42%, rgba(255,255,255,0.45) 50%, rgba(255,255,255,0.12) 58%)',
});

type SurfacePalette = ReturnType<typeof surfacePalette>;

declare module '@mui/material/styles' {
  interface Palette extends SurfacePalette {
    review: ReviewPalette;
    status: StatusPalette;
  }
  interface PaletteOptions extends Partial<SurfacePalette> {
    review?: ReviewPalette;
    status?: StatusPalette;
  }
  interface Palette {
    customGrey: PaletteColor & {
      dark2: string;
      divider: string;
      background: string;
      subListBackground: string;
      sidebarSubListSelectedBackground: string;
      sidebarSubListSelectedBorder: string;
      ultraLight: string;
      grey1: string;
      grey2: string;
      grey3: string;
    };
  }
  interface PaletteOptions {
    customGrey: PaletteColorOptions & {
      dark2: string;
      divider: string;
      background: string;
      subListBackground: string;
      sidebarSubListSelectedBackground: string;
      sidebarSubListSelectedBorder: string;
      ultraLight: string;
      grey1: string;
      grey2: string;
      grey3: string;
    };
  }
}

export const createAppTheme = (mode: 'dark' | 'light') =>
  createTheme({
    breakpoints: {
      values: customBreakpoints,
    },
    palette: {
      mode,
      // Light: darker shades so text and white-on-colour labels reach 4.5:1.
      primary:
        mode === 'dark'
          ? { main: '#008adc', light: '#00a0ff' }
          : { main: '#2957CB', light: '#4787ff', dark: '#1e42a8' },
      error:
        mode === 'dark'
          ? { main: '#F83232', light: '#FF7373' }
          : { main: '#d32f2f', light: '#d64545' },
      review: reviewPalette(mode),
      status: statusPalette(mode),
      ...surfacePalette(mode),
      customGrey: {
        dark: mode === 'dark' ? '#20293f' : '#e1eaf8',
        dark2: mode === 'dark' ? '#0e172d' : '#f8faff',
        main: mode === 'dark' ? '#20293f' : '#eef3fc',
        light: mode === 'dark' ? '#20293f' : '#ffffff',
        divider:
          mode === 'dark'
            ? 'linear-gradient(90deg, #20293f, white, #20293f)'
            : 'linear-gradient(90deg, #eef3fc, #94a3b8, #eef3fc)',
        background: mode === 'dark' ? '#0e172d' : '#f4f7fc',
        subListBackground: mode === 'dark' ? '#697382' : '#c6d6f3',
        sidebarSubListSelectedBackground: mode === 'dark' ? '#848D9A' : '#d1e1ff',
        sidebarSubListSelectedBorder: mode === 'dark' ? '#788495' : '#a3c3ff',
        ultraLight: mode === 'dark' ? '#ffffffbf' : '#1f2937',
        grey1: mode === 'dark' ? '#B4B3B3' : '#4b5563',
        grey2: mode === 'dark' ? '#D0D1D2' : '#374151',
        grey3: mode === 'dark' ? '#ffffff14' : '#00000014',
      },
      warning: {
        main: mode === 'dark' ? '#FFC700' : '#b26a00',
      },
      info: {
        main: mode === 'dark' ? '#00C2FF' : '#0369a1',
      },
      background: {
        default: mode === 'dark' ? '#0e172d' : '#f4f7fc',
        paper: mode === 'dark' ? '#20293f' : '#ffffff',
      },
    },
    typography: {
      button: {
        textTransform: 'capitalize',
      },
    },
    shape: {
      borderRadius: BORDER_RADIUS,
    },
    transitions: {
      easing: {
        easeOut: EASE_OUT,
        sharp: EASE_OUT,
        easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
        easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      },
      duration: {
        shortest: 100,
        shorter: 150,
        short: 200,
        standard: 200,
        complex: 250,
        enteringScreen: 200,
        leavingScreen: 160,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: ({ palette }) => ({
          ...scrollbarStyles(palette),
          '@media (prefers-reduced-motion: reduce)': {
            '*, *::before, *::after': {
              animationDuration: '0.01ms !important',
              animationIterationCount: '1 !important',
              transitionDuration: '0.01ms !important',
              scrollBehavior: 'auto !important',
            },
          },
        }),
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            textTransform: 'none',
            transition: PRESS_TRANSITION,
            '&:active:not(.Mui-disabled)': {
              transform: `scale(${PRESS_SCALE})`,
            },
          },
          contained: {
            color: '#ffffff',
            '&.Mui-disabled': {
              color:
                mode === 'light' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.3)',
              backgroundColor:
                mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
            },
          },
          containedPrimary: {
            color: '#ffffff',
            '&.Mui-disabled': {
              color:
                mode === 'light' ? 'rgba(0, 0, 0, 0.38)' : 'rgba(255, 255, 255, 0.3)',
              backgroundColor:
                mode === 'light' ? 'rgba(41, 87, 203, 0.35)' : 'rgba(0, 138, 220, 0.25)',
            },
          },
          startIcon: {
            marginRight: 6,
            marginLeft: -2,
          },
          endIcon: {
            marginLeft: 6,
            marginRight: -2,
          },
        },
        defaultProps: {
          disableElevation: true,
          disableRipple: false,
        },
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            transition: PRESS_TRANSITION,
            '&:active:not(.Mui-disabled)': {
              transform: `scale(${PRESS_SCALE})`,
            },
          },
        },
      },
      MuiPaper: {
        defaultProps: {
          elevation: 0,
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
          rounded: {
            borderRadius: BORDER_RADIUS,
          },
          elevation1: {
            boxShadow: softElevationShadow(mode),
          },
          elevation2: {
            boxShadow: elevationShadow(mode),
          },
          elevation3: {
            boxShadow: elevationShadow(mode),
          },
          elevation4: {
            boxShadow: elevationShadow(mode),
          },
          elevation8: {
            boxShadow: elevationShadow(mode),
          },
          elevation16: {
            boxShadow: elevationShadow(mode),
          },
          elevation24: {
            boxShadow: elevationShadow(mode),
          },
        },
      },
      MuiDialog: {
        defaultProps: {
          transitionDuration: { enter: 200, exit: 160 },
        },
        styleOverrides: {
          paper: {
            borderRadius: DIALOG_RADIUS,
            boxShadow: elevationShadow(mode),
          },
        },
      },
      MuiDialogActions: {
        styleOverrides: {
          root: {
            padding: 24,
            gap: 8,
            // Whole buttons move to a new row on narrow screens; labels never wrap.
            flexWrap: 'wrap',
            '& .MuiButton-root': {
              whiteSpace: 'nowrap',
            },
            // Full-width action buttons share the row equally but never get
            // narrower than their label, so a row has one height.
            '& > .MuiButton-fullWidth': {
              flex: '1 1 0',
              minWidth: 'max-content',
            },
          },
          // The gap above spaces the buttons; MUI's extra left margin would
          // double it and misalign a wrapped second row.
          spacing: {
            '& > :not(style) ~ :not(style)': {
              marginLeft: 0,
            },
          },
        },
      },
      MuiMenu: {
        defaultProps: {
          transitionDuration: { enter: 150, exit: 120 },
        },
        styleOverrides: {
          paper: {
            borderRadius: MENU_RADIUS,
            boxShadow: softElevationShadow(mode),
            backgroundImage: 'none',
          },
          list: {
            paddingTop: 6,
            paddingBottom: 6,
          },
        },
      },
      MuiPopover: {
        styleOverrides: {
          paper: {
            borderRadius: MENU_RADIUS,
            boxShadow: softElevationShadow(mode),
            backgroundImage: 'none',
          },
        },
      },
      MuiTooltip: {
        defaultProps: {
          enterDelay: 400,
          enterNextDelay: 0,
          leaveDelay: 0,
          arrow: false,
        },
        styleOverrides: {
          tooltip: {
            borderRadius: 6,
            fontSize: '0.75rem',
            padding: '6px 10px',
            transition: `opacity 125ms ${EASE_OUT}, transform 125ms ${EASE_OUT}`,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            borderRight: `1px solid ${
              mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(41,87,203,0.16)'
            }`,
            ...(mode === 'light' && { backgroundColor: '#f8faff' }),
            boxShadow: 'none',
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            transition: `background-color 100ms ${EASE_OUT}, color 100ms ${EASE_OUT}`,
            '&.Mui-selected': {
              backgroundColor:
                mode === 'dark' ? 'rgba(0, 138, 220, 0.16)' : 'rgba(41, 87, 203, 0.1)',
              '&:hover': {
                backgroundColor:
                  mode === 'dark' ? 'rgba(0, 138, 220, 0.22)' : 'rgba(41, 87, 203, 0.14)',
              },
            },
          },
        },
      },
      MuiFilledInput: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
            overflow: 'hidden',
            '&:before': { display: 'none' },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: BORDER_RADIUS,
          },
        },
      },
      MuiTextField: {
        defaultProps: {
          size: 'small',
          variant: 'outlined',
        },
      },
      MuiSkeleton: {
        defaultProps: {
          animation: 'wave',
        },
      },
      MuiContainer: {
        defaultProps: { maxWidth: 'lg' },
      },
      MuiTableCell: {
        styleOverrides: { root: { border: 'none' } },
      },
      MuiAppBar: {
        defaultProps: {
          elevation: 0,
          color: 'transparent',
        },
        styleOverrides: {
          root: {
            backgroundImage: 'none',
            boxShadow: 'none',
          },
        },
      },
    },
  });

const defaultTheme = createAppTheme('dark');
export default defaultTheme;
